import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../lib/auth";

// AppEmptyState — wiederverwendbares Empty-Pattern für alle /app/*-Routen,
// die einen verknüpften Erzähler benötigen.
//
// Verwendung:
//   <AppEmptyState
//     icon="phone"
//     title="Noch kein Erzähler-Profil verknüpft."
//     body="Anrufe gehören zu einem Erzähler-Profil, das per Telefonnummer
//           identifiziert wird."
//   />
//
// Lädt Admin → /admin, sonst → /onboarding. CTA-Label wird automatisch
// gewählt; mit `ctaLabel`/`ctaTo` lässt sich beides überschreiben.

type IconName = "phone" | "calls" | "book" | "photo" | "family" | "default";

export default function AppEmptyState({
  icon = "default",
  title,
  body,
  ctaLabel,
  ctaTo,
  secondary,
}: {
  icon?: IconName;
  title: string;
  body: ReactNode;
  ctaLabel?: string;
  ctaTo?: string;
  secondary?: ReactNode;
}) {
  const { isAdmin } = useAuth();
  const to = ctaTo ?? (isAdmin ? "/admin" : "/onboarding");
  const label =
    ctaLabel ?? (isAdmin ? "Zum Admin-Dashboard" : "Onboarding fortsetzen");

  return (
    <div style={{ padding: "0 22px 90px" }}>
      <div
        style={{
          background: "var(--paper-50)",
          border: "1px solid rgba(43,26,15,0.10)",
          borderRadius: 14,
          padding: "26px 22px",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative oval-ish badge with icon */}
        <div
          aria-hidden
          style={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            background: "var(--paper-200)",
            border: "1px solid rgba(43,26,15,0.10)",
            margin: "0 auto 14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--oxblood)",
          }}
        >
          <EmptyIcon name={icon} />
        </div>

        <div
          style={{
            fontFamily: "var(--f-display)",
            fontStyle: "italic",
            fontWeight: 400,
            fontSize: 22,
            lineHeight: 1.15,
            color: "var(--ink)",
            margin: "0 0 8px",
            letterSpacing: "-0.01em",
          }}
        >
          {title}
        </div>
        <p
          style={{
            fontFamily: "var(--f-body)",
            fontSize: 15,
            lineHeight: 1.55,
            color: "var(--ink-soft)",
            margin: "0 auto 18px",
            maxWidth: 340,
          }}
        >
          {body}
        </p>

        <Link
          to={to}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            background: "var(--ink)",
            color: "var(--paper)",
            padding: "11px 22px",
            borderRadius: 999,
            fontFamily: "var(--f-ui)",
            fontSize: 13.5,
            fontWeight: 600,
            textDecoration: "none",
            letterSpacing: "0.02em",
          }}
        >
          {label}
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            aria-hidden
          >
            <path d="M5 12h14M13 5l7 7-7 7" />
          </svg>
        </Link>

        {secondary && (
          <div
            style={{
              marginTop: 14,
              fontFamily: "var(--f-body)",
              fontStyle: "italic",
              fontSize: 12.5,
              color: "var(--ink-faint)",
            }}
          >
            {secondary}
          </div>
        )}
      </div>
    </div>
  );
}

function EmptyIcon({ name }: { name: IconName }) {
  const common = {
    width: 24,
    height: 24,
    viewBox: "0 0 24 24",
    fill: "none" as const,
    stroke: "currentColor",
    strokeWidth: 1.6,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };
  if (name === "phone" || name === "calls")
    return (
      <svg {...common}>
        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 16.92z" />
      </svg>
    );
  if (name === "book")
    return (
      <svg {...common}>
        <path d="M4 4h12a3 3 0 013 3v13H7a3 3 0 01-3-3V4z" />
        <path d="M4 4v13a3 3 0 003 3" />
      </svg>
    );
  if (name === "photo")
    return (
      <svg {...common}>
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <circle cx="9" cy="11" r="2" />
        <path d="M21 15l-5-5L5 19" />
      </svg>
    );
  if (name === "family")
    return (
      <svg {...common}>
        <circle cx="9" cy="8" r="3" />
        <circle cx="17" cy="9" r="2.5" />
        <path d="M3 20c0-3 3-5 6-5s6 2 6 5" />
        <path d="M15 18c.5-1.5 2-3 4-3s3 1 3 3" />
      </svg>
    );
  return (
    <svg {...common}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v4M12 16h.01" />
    </svg>
  );
}
